import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { CURRENT_PROJECTS, PROJECTS, SERVICES, SITE, TECH_STACKS } from "./data/siteData";
import "./style.css";

const NAV_ITEMS = [
    { href: "#services", label: "Services" },
    { href: "#portfolio", label: "Portfolio" },
    { href: "#tech", label: "Tech Stack" },
];

// 모든 내부 메뉴가 동일한 스크롤 동작을 사용하도록 한곳에서 처리합니다.
function scrollToSection(event, target) {
    event.preventDefault();
    document.querySelector(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Nav() {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    return (
        <nav className={`nav ${scrolled ? "scrolled" : ""}`} aria-label="주요 메뉴">
            <div className="container nav-inner">
                <a href="#home" className="brand" onClick={(event) => scrollToSection(event, "#home")}>
                    <img src="/entsys_main_logo.png" alt={SITE.brand.name} className="brand-logo" />
                </a>
                <div className="nav-actions">
                    {NAV_ITEMS.map((item) => (
                        <a key={item.href} href={item.href} className="muted" onClick={(event) => scrollToSection(event, item.href)}>
                            {item.label}
                        </a>
                    ))}
                    <a href="#contact" className="btn primary" onClick={(event) => scrollToSection(event, "#contact")}>Contact</a>
                </div>
            </div>
        </nav>
    );
}

function Section({ id, className = "", children }) {
    return <section id={id} className={`container ${className}`.trim()}>{children}</section>;
}

function SectionHeading({ label, title, subtitle }) {
    return (
        <>
            <span className="section-label">{label}</span>
            <h2>{title}</h2>
            {subtitle && <p className="muted top-gap section-subtitle">{subtitle}</p>}
        </>
    );
}

// 아래 컴포넌트 순서는 실제 홈페이지의 섹션 순서와 동일합니다.
function Hero() {
    return (
        <div id="home" className="hero">
            <div className="overlay" />
            <div className="container hero-content">
                <img src={SITE.hero.image} alt="ENTSYS 기업용 IT 시스템 구축" className="hero-graphic" />
                <h1>{SITE.hero.title}</h1>
                <p>{SITE.hero.subtitle}</p>
                <div className="cta">
                    <a href="#contact" className="btn primary">{SITE.hero.ctaPrimary}</a>
                    <a href="#services" className="btn outline">{SITE.hero.ctaSecondary}</a>
                </div>
            </div>
        </div>
    );
}

function Services() {
    return (
        <Section id="services" className="pad-y">
            <SectionHeading label="OUR SERVICES" title="Our Services" subtitle={SITE.brand.tagline} />
            <div className="cards">
                {SERVICES.map((service) => (
                    <article key={service.title} className="card">
                        <h3 className="card-title">{service.title}</h3>
                        <p className="card-desc">{service.description}</p>
                    </article>
                ))}
            </div>
        </Section>
    );
}

function CurrentProjects() {
    return (
        <div className="current-projects" aria-label="현재 진행 중인 프로젝트">
            <div className="current-projects-heading">
                <span className="status-dot" aria-hidden="true" />
                <div><span>CURRENT PROJECTS</span>{/*<strong>진행 중인 프로젝트</strong>*/}</div>
                <em>{/*{CURRENT_PROJECTS.length}*/}</em>
            </div>
            <div className="current-projects-list">
                {CURRENT_PROJECTS.map((project) => (
                    <article key={project.title}>
                        <span>{project.category}</span>
                        <strong>{project.title}</strong>
                        <time>{project.period}</time>
                    </article>
                ))}
            </div>
        </div>
    );
}

function Portfolio() {
    return (
        <Section id="portfolio" className="pad-y portfolio-section">
            <SectionHeading label="PORTFOLIO" title="Portfolio" subtitle="다양한 산업 분야에서 검증된 실행력과 기술 전문성" />
            <CurrentProjects />
            <div className="portfolio-grid">
                {PROJECTS.map((project) => (
                    <article key={`${project.title}-${project.period}`} className="portfolio-card">
                        <div className="portfolio-image" style={{ "--project-accent": project.accent }} aria-hidden="true">
                            <span>{project.image}</span>
                        </div>
                        <div className="portfolio-content">
                            <header className="portfolio-header">
                                <span className="portfolio-category">{project.category}</span>
                                <h3 className="portfolio-title">{project.title}</h3>
                            </header>
                            <p className="portfolio-desc">{project.description}</p>
                            <div className="portfolio-meta"><span className="portfolio-period">{project.period}</span></div>
                            <div className="portfolio-tech">
                                {project.tech.map((technology) => <span key={technology} className="tech-badge">{technology}</span>)}
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </Section>
    );
}

function TechStack() {
    return (
        <Section id="tech" className="pad-y tech-section">
            <SectionHeading label="TECHNOLOGY" title="Tech Stack" subtitle="최신 기술 스택과 검증된 아키텍처로 안정적이고 확장 가능한 솔루션을 제공합니다" />
            <div className="tech-categories">
                {Object.entries(TECH_STACKS).map(([category, technologies]) => (
                    <article key={category} className="tech-category">
                        <h3 className="tech-category-title">{category}</h3>
                        <div className="tech-items">
                            {technologies.map((technology) => (
                                <div key={technology.name} className="tech-item">
                                    <span className="tech-icon" aria-hidden="true">{technology.icon}</span>
                                    <span className="tech-name">{technology.name}</span>
                                </div>
                            ))}
                        </div>
                    </article>
                ))}
            </div>
        </Section>
    );
}

function About() {
    return (
        <Section id="about" className="pad-y about-section">
            <div className="about-panel">
                <span className="section-label align-left">ABOUT ENTSYS</span>
                <h3>About ENTSYS</h3>
                <p className="muted about-desc">ENTSYS는 IT 인력 소싱과 기술 컨설팅을 전문으로 하는 테크 파트너입니다. 금융, 제조, 물류, 유통 등 다양한 산업 분야에서 축적한 경험을 바탕으로, 고객의 비즈니스 목표 달성을 위한 최적의 기술 솔루션을 제공합니다.</p>
                <p className="muted about-desc">또한 자체 서비스 개발을 통해 시장의 변화를 빠르게 읽고 대응하며, 지속적인 기술 혁신과 품질 향상에 집중하고 있습니다. 우리는 단순한 개발 대행이 아닌, 고객과 함께 성장하는 진정한 파트너가 되고자 합니다.</p>
                <div className="about-values">
                    <div className="value-item"><strong>명확한 목표</strong><span>구체적인 성과 지표와 로드맵 제시</span></div>
                    <div className="value-item"><strong>빠른 실행</strong><span>애자일 방법론 기반의 신속한 개발</span></div>
                    <div className="value-item"><strong>지속 가능한 성장</strong><span>장기적 관점의 기술 지원과 유지보수</span></div>
                </div>
            </div>
        </Section>
    );
}

function Contact() {
    const phoneLink = SITE.contact.phone.replaceAll("-", "");
    return (
        <Section id="contact" className="pad-y contact-gap">
            <div>
                <SectionHeading label="CONTACT" title="Contact" />
                <address className="contact-list vstack-8">
                    <div>📧 <a href={`mailto:${SITE.contact.email}`}>{SITE.contact.email}</a></div>
                    <div>📞 <a href={`tel:${phoneLink}`}>{SITE.contact.phone}</a></div>
                    <div>📍 {SITE.contact.address}</div>
                </address>
            </div>
        </Section>
    );
}

function Footer() {
    return (
        <footer className="footer">
            <div className="container footer-inner">
                <div>© {new Date().getFullYear()} {SITE.brand.name}. All rights reserved.</div>
                <div className="footer-links">
                    <a href="#about">About</a><a href="#services">Services</a><a href="#portfolio">Portfolio</a><a href="#contact">Contact</a>
                </div>
            </div>
        </footer>
    );
}

function SiteMetadata() {
    return (
        <Helmet>
            <html lang="ko" />
            <title>엔트시스 ENTSYS</title>
            <meta name="description" content="엔트시스(ENTSYS)는 웹·백엔드·시스템 구축 전문 IT 전문 기업입니다." />
            <meta name="keywords" content="엔트시스, ENTSYS, IT개발, 시스템구축, 시스템운영, IT컨설팅, IT소싱, 아웃소싱, SI, SM" />
            <meta property="og:title" content="엔트시스 ENTSYS" />
            <meta property="og:description" content="검증된 기술력과 실행력으로 비즈니스 성공을 만듭니다" />
            <meta property="og:url" content="https://entsys.net" />
            <meta property="og:type" content="website" />
            <meta property="og:image" content="https://entsys.net/og.png" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="엔트시스 ENTSYS" />
            <meta name="twitter:description" content="검증된 기술력과 실행력으로 비즈니스 성공을 만듭니다" />
            <meta name="twitter:image" content="https://entsys.net/og.png" />
            <meta name="naver-site-verification" content="3c38897ad8a68517383839ebce97c4b9c83e9c5c" />
            <link rel="canonical" href="https://entsys.net" />
        </Helmet>
    );
}

export default function App() {
    return (
        <>
            <SiteMetadata />
            <div className="app"><Nav /><Hero /><Services /><Portfolio /><TechStack /><About /><Contact /><Footer /></div>
        </>
    );
}
